import React from 'react';
import { Select, Input, Button } from 'components/common/FormItems';
import Card from 'components/common/Card';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import {
  CenterContentDiv,
  FormFlexWrapper,
  VariableWidthDiv,
  FormLabel,
} from '../SettingsFormUtils';
import {
  withUserPersonalWorkspace,
  withUserOrganisationWorkspace,
  withAccount,
  withMsgInfo,
  withTranslation,
  withUserDefaultSpace,
  withDateFormat,
  withLanguage,
  withUserPreferences,
  withNumberFormat,
  withAuth,
  withSpace,
  withDocument,
  withPersonalInfo,
  withUserLayoutChoice,
  withCountry, 
  withUserPhoneNumber,
} from 'components/hoc';
import withFileSize from 'components/hoc/FileSizeChecker/File';
import { sweetAlert } from 'components/utils';
import { updateUserNameService } from '../services/settings';
import Swal from 'sweetalert2';
import { TrashFill } from 'react-bootstrap-icons';
import { passwordStrength } from 'components/utils';
import { passwordChange } from '../../../../actions';
import { useDispatch } from 'react-redux';
import ToggleSwitch from 'components/common/ToggleSwitch';
import { useNavigate } from 'react-router-dom';
import { withPayments } from 'components/hoc/Payment';

const StyledButton = styled(Button)`
  background: #e4f1ff;
  border-radius: 5px;
  height: 38px;
  color: ${(props) => props.color || '#F00'};
  border: none;
  font-size: 14px;
  line-height: 10px;
  margin-left: 25px;

  @media screen and (max-width: 768px) {
    margin-left: 0;
    margin-top: 10px;
    width: 100%;
  }
`;

const ResponsiveCard = styled(Card)`
  @media screen and (max-width: 768px) {
    padding: 15px !important;
    width: 100% !important;
  }
`;

const ResponsiveFlexWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 3%;
  align-items: flex-end;
  padding: 1%;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;

    > div {
      width: 100% !important;
    }
  }
`;

const ResponsiveButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  height: 55px;

  @media screen and (max-width: 768px) {
    height: auto;
    width: 100%;
    gap: 10px;

    button {
      flex: 1;
    }
  }
`;

const ResponsiveEmailWrapper = styled(VariableWidthDiv)`
  display: flex;
  flex-direction: row;

  @media screen and (max-width: 768px) {
    flex-direction: column !important;

    > div {
      width: 100% !important;
      margin-bottom: 10px;
      margin-left: 0 !important;
    }
  }
`;

const ResponsiveToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 20px;
  gap: 15px;

  @media screen and (max-width: 768px) {
    align-items: flex-start;

    > label {
      margin-left: 0 !important;
      font-size: 14px;
    }
  }
`;

const SettingsUserTab = (props) => {
  const [values, setValues] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const dispatch = useDispatch();
  const [passwordChangeLoading, setPasswordChangeLoading] = React.useState(false);
  const [phoneNumberLoading, setPhoneNumberLoading] = React.useState(false);
  const {
    tr,
    trObj, 
    country, 
    otp_browser_dependent,
    numberFormatLocaleOptions,
    handlePreferenceChange,
    userPrefDateFormat,
    updateUserPreferences,
    userPreNumberFormat,
    selectedLocale,
    languageOptions,
    account,
    setMsgInfo,
    toggleOTPBrowserPreference,
    userPhoneNumber,
    updateUserPhoneNumber,
  } = props;
  const navigate = useNavigate();

  const workspace_type =
    account.user.space_type === 'P' ? 'personal' : 'organisation';

 React.useEffect(() => {
    if (userPhoneNumber) {
      const hasPhoneCountryCode = userPhoneNumber.phone_country_code !== undefined && userPhoneNumber.phone_country_code !== null;
      const hasPhoneNumber = userPhoneNumber.phone_number !== undefined && userPhoneNumber.phone_number !== null;
      
      if (hasPhoneCountryCode || hasPhoneNumber) {
        setValues(prev => ({
          ...prev,
          phoneCountry: userPhoneNumber.phone_country_code || '',
          phoneNumber: userPhoneNumber.phone_number || '',
        }));
      }
    }
  }, [userPhoneNumber?.phone_country_code, userPhoneNumber?.phone_number]);


  const getOptions = () => {
    const { dateFormatList, userPreLang } = props;
    return dateFormatList.map((item) => ({
      id: item.id,
      name: item[`long_description_${userPreLang}`],
    }));
  };

  const handleChange = (event) => {
    setValues({ ...values, [event.target.name]: event.target.value });
  };


    // Handle phone number save
  const handlePhoneNumberSave = async () => {
    try {
      setPhoneNumberLoading(true);
      
      if (!values.phoneCountry && !values.phoneNumber) {
        setMsgInfo({
          success: false,
          msg: [tr('Please fill in at least one field')],
        });
        return;
      }

      const payload = {
        phone_country_code: values.phoneCountry || '', 
        phone_number: values.phoneNumber || '',
      };

      await updateUserPhoneNumber(payload);
      setMsgInfo({
        success: true,
        msg: [tr('Phone number updated successfully')],
      });
    } catch (error) {
      setMsgInfo({
        success: false,
        msg: [tr('Failed to update phone number')],
      });
    } finally {
      setPhoneNumberLoading(false);
    }
  };


  const handleSubmitUsername = () => {
    const { tr } = props;
    if (values.userName && values.userName !== account.user.username) {
      sweetAlert({
        title: tr('Change Username'),
        content: tr('Are you sure you want to change username?'),
        tr,
        actions: [
          {
            label: tr('Yes'),
            onClick: async () => {
              try {
                setLoading(true);
                updateUserNameService({
                  id: account.user.user_id,
                  org_id: account.user.org_id,
                  username: values?.userName,
                }).then((data) => {
                  if (data) {
                    dispatch(
                      setMsgInfo({
                        success: true,
                        msg: [`${tr('Your username has been changed')}`],
                        navigationCounter: 1,
                      }),
                    );
                  }
                  setLoading(false);
                });
              } catch (e) {
                Swal.fire(tr('Failed!'), tr('there was an error.'), 'error');
                setLoading(false);
              }
            },
          },
        ],
      });
    }
  };


  const handleSubmitPasswordChange = async () => {
    let errNewPassword = values.newPassword ? '' : 'This field is required';
    let errCurrentPassword = values.currentPassword ? '' : 'This field is required';

    setErrors({
      ...errors,
      currentPassword: errCurrentPassword,
      newPassword: errNewPassword,
    });

    if (errCurrentPassword || errNewPassword) {
      // if none of this field are filled, return
      return;
    }

    let old_password = values?.currentPassword;
    let new_password = values?.newPassword;

    let valid =
      !!new_password && !!old_password && !passwordStrength(new_password).hasError;

    if (!valid) {
      const { errors } = passwordStrength(new_password);
      setMsgInfo({
        success: false,
        msg: errors,
      });
      return;
    }

    // here make the api call, to actually reset the password
    setPasswordChangeLoading(true);
    try {
      await dispatch(
        passwordChange({
          old_password: values?.currentPassword,
          new_password: values?.newPassword,
        }),
      );
      
      // Success message
      setMsgInfo({
        success: true,
        msg: [tr('Password changed successfully')],
      });
      
      // Clear fields
      setValues({ ...values, newPassword: '', currentPassword: '' });
      setErrors({});
    } catch (error) {
      // Error message
      setMsgInfo({
        success: false,
        msg: [tr('Failed to change password. Please check your current password.')],
      });
    } finally {
      setPasswordChangeLoading(false);
    }
  };


  const deleteAccount = () => {
    const { logout, deleteUserById, tr } = props; 
    let myUser = props.user.data.length > 0 ? props.user.data[0] : null;
    if (myUser?.id) {
      sweetAlert({
        title: tr('Delete Account'),
        content: tr('Are you sure you want to delete account?'),
        tr,
        actions: [
          {
            label: tr('Yes'),
            onClick: async () => {
              try {
                await deleteUserById(myUser.id);
                logout();
              } catch (e) {}
            },
          },
        ],
      });
    }
  };

  const resetPassword = () => {
    setValues({ ...values, newPassword: '', currentPassword: '' });
    setErrors({});
  };

  const handleOtpBrowserDependentChange = (e) => {
    const value = e.target.checked;
    toggleOTPBrowserPreference({ target: { name: 'otp_browser_dependent', value } });
  };

// Get unique country options with calling codes
const getCountryCallingCodeOptions = () => {
  if (!country || country.length === 0) return [];
  
  // Get unique countries by tel_code
  const uniqueCountries = Array.from(
    new Set(country.map((c) => c.tel_code))
  ).map((tel_code) => {
    const countryData = country.find((c) => c.tel_code === tel_code);
    // Add + prefix if not already present
    const formattedTelCode = countryData.tel_code.startsWith('+') 
      ? countryData.tel_code 
      : `+${countryData.tel_code}`;
    
    return {
      id: formattedTelCode, 
      name: `${trObj(countryData)} (${formattedTelCode})`,
      tel_code: formattedTelCode,
    };
  });
  
  // Sort alphabetically by name
  return uniqueCountries.sort((a, b) => a.name.localeCompare(b.name));
};


  // show messages for updating user preferences
  const handleUpdateUserPreferences = async () => {
    try {
      await updateUserPreferences();
      setMsgInfo({
        success: true,
        msg: [tr('User preferences updated successfully')],
      });
    } catch (error) {
      setMsgInfo({
        success: false,
        msg: [tr('Failed to update user preferences')],
      });
    }
  };

  return (
    <>
      <ResponsiveCard
        cardHeader=""
        width="100%"
        paddingRight="7%"
        paddingLeft="3%"
        paddingTop="3%"
        paddingBottom="3%"
      >
        <FormLabel>{tr('User Type')}</FormLabel>
        <Input
          name="userType"
          type="text"
          placeholder=""
          onChange={handleChange}
          value={workspace_type === 'personal' ? 'Person' : 'Organisation'}
          disabled
        />
        <FormLabel>{tr('UserName')}</FormLabel>
        <Input
          name="userName"
          type="text"
          placeholder={account.user.username || 'Username'}
          onChange={handleChange}
          value={values?.userName}
          required
        />
        <CenterContentDiv>
          <Button
            actionButton
            name={tr('Save')}
            style={{ width: '130px' }}
            onClick={handleSubmitUsername}
            isLoading={loading}
          />
        </CenterContentDiv>
      </ResponsiveCard>

      <ResponsiveCard
        cardHeader=""
        width="100%"
        paddingRight="7%"
        paddingLeft="3%"
        paddingTop="3%"
        paddingBottom="3%"
      >
        <FormLabel>{tr('Email Address')}</FormLabel>
        <ResponsiveEmailWrapper flexDirection="row" alignItems="flex-start">
          <div style={{ flexGrow: 1 }}>
            <Input
              name="Email"
              type="email"
              placeholder={account.user.email || 'yourname@domain.com'}
              disabled
            />
          </div>
          <div style={{ flexShrink: 1 }}>
            <StyledButton
              reactIcon={<TrashFill color="#F00" size={12} className="mr-2" />}
              onClick={deleteAccount}
              name={tr('Delete Account')}
            />
          </div>
        </ResponsiveEmailWrapper>

        <FormLabel>{tr('Phone number for receiving OTP via SMS (optional)')}</FormLabel>
        <FormFlexWrapper padding={'1%'} style={{ 
          display: 'flex',
          flexWrap: 'wrap',
          gap: '3%'
        }}>
          <VariableWidthDiv width="25%" marginRight="3%" style={{ 
            minWidth: window.innerWidth <= 768 ? '100%' : 'auto',
            marginRight: window.innerWidth <= 768 ? '0' : '3%',
            marginBottom: window.innerWidth <= 768 ? '15px' : '0'
          }}>
            <FormLabel>{tr('Country')}</FormLabel>
            <Select
              name="phoneCountry"
              id="phoneCountry"
              value={values?.phoneCountry || ''}
              optionDefaultValue={tr('Select Country')}
              onChange={handleChange}
              options={getCountryCallingCodeOptions()}
            />
          </VariableWidthDiv>
          <VariableWidthDiv width="45%" marginRight="3%" style={{ 
            minWidth: window.innerWidth <= 768 ? '100%' : 'auto',
            marginRight: window.innerWidth <= 768 ? '0' : '3%',
            marginBottom: window.innerWidth <= 768 ? '15px' : '0'
          }}>
            <FormLabel>{tr('Phone number')}</FormLabel>
            <Input
              name="phoneNumber"
              type="tel"
              placeholder={tr('Enter phone number')}
              onChange={handleChange}
              value={values?.phoneNumber || ''}
            />
          </VariableWidthDiv>
          <VariableWidthDiv width="15%" style={{ 
            paddingTop: window.innerWidth <= 768 ? '0' : '40px',
            minWidth: window.innerWidth <= 768 ? '100%' : 'auto'
          }}>
            <Button
              actionButton
              name={tr('Save')}
              style={{ width: '100%', height: '38px' }}
              onClick={handlePhoneNumberSave}
              isLoading={phoneNumberLoading}
            />
          </VariableWidthDiv>
        </FormFlexWrapper>

        <ResponsiveToggleWrapper>
          <ToggleSwitch
            checked={otp_browser_dependent}
            name={'otp_browser_dependent'}
            handleChange={handleOtpBrowserDependentChange}
          />
          <FormLabel style={{ marginBottom: '0', marginLeft: '0' }}>
            {otp_browser_dependent 
              ? tr('Disable browser ID to reduce the frequency of OTP requests on login')
              : tr('Enable browser ID to reduce the frequency of OTP requests on login')
            }
          </FormLabel>
        </ResponsiveToggleWrapper>

        <FormLabel>{tr('Change Password')}</FormLabel>
        <ResponsiveFlexWrapper>
          <div style={{ width: '35%' }}>
            <FormLabel>{tr('Current Password')}</FormLabel>
            <Input
              name="currentPassword"
              type="password"
              onChange={handleChange}
              value={values?.currentPassword || ''}
              autoComplete="off"
              showRequiredAsterisk
              error={errors?.currentPassword}
              tinyInfo={errors?.currentPassword}
              placeholder="Enter current password"
            />
          </div>
          <div style={{ width: '35%' }}>
            <FormLabel>{tr('New Password')}</FormLabel>
            <Input
              name="newPassword"
              type="password"
              onChange={handleChange}
              value={values?.newPassword || ''}
              autoComplete="off"
              showRequiredAsterisk
              error={errors?.newPassword}
              tinyInfo={errors?.newPassword}
              placeholder="Enter new password"
            />
          </div>
          <ResponsiveButtonGroup>
            <Button
              actionButton
              name={tr('Save')}
              style={{ width: '100px', height: '38px' }}
              onClick={handleSubmitPasswordChange}
              isLoading={passwordChangeLoading}
            />
            <Button
              cancelButton
              name={tr('Clear')}
              style={{ width: '130px', color: '#000000', height: '38px' }}
              onClick={resetPassword}
              isLoading={false}
            />
          </ResponsiveButtonGroup>
        </ResponsiveFlexWrapper>
      </ResponsiveCard>

      <ResponsiveCard
        cardHeader=""
        width="100%"
        paddingRight="7%"
        paddingLeft="3%"
        paddingTop="3%"
        paddingBottom="3%"
      >
        <FormFlexWrapper style={{ 
          display: 'flex',
          flexWrap: 'wrap',
          gap: window.innerWidth <= 768 ? '15px' : '2%'
        }}>
          <VariableWidthDiv width="40%" style={{ 
            minWidth: window.innerWidth <= 768 ? 'calc(50% - 7.5px)' : '40%'
          }}>
            <FormLabel>{tr('Choose Language')}</FormLabel>
            <Select
              name="selectedLocale"
              id="selectedLocale"
              value={selectedLocale}
              optionDefaultValue={tr('Choose Language')}
              onChange={handlePreferenceChange}
              options={languageOptions.filter(({ id }) =>
                ['eng', 'deu'].includes(id),
              )}
            />
          </VariableWidthDiv>
          <VariableWidthDiv width="40%" style={{ 
            minWidth: window.innerWidth <= 768 ? 'calc(50% - 7.5px)' : '40%'
          }}>
            <FormLabel>{tr('Choose Date Format')}</FormLabel>
            <Select
              name="dateFormatId"
              id="dateFormat"
              value={userPrefDateFormat}
              optionDefaultValue={tr('Choose Date Format')}
              onChange={handlePreferenceChange}
              options={getOptions()}
            />
          </VariableWidthDiv>
          <VariableWidthDiv width="40%" style={{ 
            minWidth: window.innerWidth <= 768 ? '100%' : '40%'
          }}>
            <FormLabel>{tr('Choose Number Format')}</FormLabel>
            <Select
              name="numberFormat"
              value={userPreNumberFormat}
              optionDefaultValue={tr('Choose Number Format')}
              onChange={handlePreferenceChange}
              localeOptions={numberFormatLocaleOptions}
            />
          </VariableWidthDiv>
        </FormFlexWrapper>
        <CenterContentDiv>
          <Button
            actionButton
            name={tr('Save')}
            style={{ width: '130px' }}
            onClick={handleUpdateUserPreferences}
            isLoading={props.isSubmitting}
          />
        </CenterContentDiv>
      </ResponsiveCard>
    </>
  );
};

export default compose(
  withTranslation,
  withDateFormat,
  withNumberFormat,
  withLanguage,
  withUserPreferences,
  withCountry, 
  withUserPersonalWorkspace,
  withUserOrganisationWorkspace,
  withUserLayoutChoice,
  withAccount,
  withMsgInfo,
  withUserDefaultSpace,
  withAuth,
  withSpace,
  withDocument,
  withPersonalInfo,
  withFileSize,
  withMsgInfo,
  withPayments,
  withUserPhoneNumber,
)(SettingsUserTab);
